import { useCallback, useMemo, useRef, useState } from "react";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";
import { toLocalTimeDisplay } from "../../utils/dateUtils";
import useStatusColors from "../../hooks/useStatusColors";
import UnscheduledTaskList from "./UnscheduledTaskList";
import "./HourlyCalendar.css";

const SLOT_HEIGHT = 60;
const MIN_TASK_HEIGHT = 30;
const SNAP_MINUTES = 30;

function parseTimeToMinutes(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(":").map(Number);
  return h * 60 + (m || 0);
}

function minutesToTimeStr(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatTimeDisplay(timeStr) {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${String(m).padStart(2, "0")} ${period}`;
}

export default function HourlyCalendar({
  tasks,
  workStartTime,
  workEndTime,
  workflowConnections,
  isAdmin,
  onReorder,
  onTaskClick,
}) {
  const [draggingId, setDraggingId] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [ghostTop, setGhostTop] = useState(null);
  const [dependencyConflicts, setDependencyConflicts] = useState([]);
  const [toast, setToast] = useState("");
  const calendarRef = useRef(null);
  const toastTimerRef = useRef(null);
  const { getStatusStyle } = useStatusColors();

  const workStartMins = parseTimeToMinutes(workStartTime || "09:00");
  const workEndMins = parseTimeToMinutes(workEndTime || "18:00");
  const totalMins = workEndMins - workStartMins;
  const OVERTIME_BUFFER_HOURS = 3;
  const calendarHeight =
    (totalMins / 60) * SLOT_HEIGHT + OVERTIME_BUFFER_HOURS * SLOT_HEIGHT;

  const hourSlots = useMemo(() => {
    const slots = [];
    for (let m = workStartMins; m <= workEndMins; m += 60) {
      slots.push({
        minutes: m,
        label: formatTimeDisplay(minutesToTimeStr(m)),
      });
    }
    return slots;
  }, [workStartMins, workEndMins]);

  const getTaskStyle = useCallback(
    (task) => {
      if (!task.scheduledStartTime) return null;

      const startDate = new Date(task.scheduledStartTime);
      const startMins = startDate.getHours() * 60 + startDate.getMinutes();

      const durationMins =
        task.durationUnit === "Hours"
          ? (task.estimatedDuration || 1) * 60
          : task.durationUnit === "Minutes"
            ? task.estimatedDuration || 1
            : (task.estimatedDuration || 1) * (workEndMins - workStartMins);

      const top = Math.max(0, ((startMins - workStartMins) / 60) * SLOT_HEIGHT);
      const height = Math.max(
        MIN_TASK_HEIGHT,
        (durationMins / 60) * SLOT_HEIGHT,
      );

      const isOvertimeTask = startMins + durationMins > workEndMins;

      return { top, height, startMins, durationMins, isOvertimeTask };
    },
    [workStartMins, workEndMins],
  );

  const overlapGroups = useMemo(() => {
    const tasksWithStyle = tasks
      .map((t) => ({ task: t, style: getTaskStyle(t) }))
      .filter(({ style }) => style !== null)
      .sort((a, b) => a.style.startMins - b.style.startMins);

    // Overlapping tasks are laid out in lanes. Two columns are not enough — a
    // third concurrent task used to be pinned to the same column as the second
    // and render straight on top of it — so each cluster of mutually
    // overlapping tasks gets as many lanes as it actually needs, and every task
    // in that cluster is sized to the cluster's lane count.
    const clusters = [];
    let current = null;
    let clusterEnd = -Infinity;

    tasksWithStyle.forEach(({ task, style }) => {
      const end = style.startMins + style.durationMins;
      if (!current || style.startMins >= clusterEnd) {
        current = [];
        clusters.push(current);
        clusterEnd = end;
      } else {
        clusterEnd = Math.max(clusterEnd, end);
      }
      current.push({ task, style });
    });

    return clusters.map((cluster) => {
      const laneEnds = [];
      const placed = cluster.map(({ task, style }) => {
        const end = style.startMins + style.durationMins;
        let column = laneEnds.findIndex((laneEnd) => laneEnd <= style.startMins);
        if (column === -1) {
          column = laneEnds.length;
          laneEnds.push(end);
        } else {
          laneEnds[column] = end;
        }
        return { task, style, column };
      });
      return placed.map((entry) => ({
        ...entry,
        columnCount: laneEnds.length,
      }));
    });
  }, [tasks, getTaskStyle]);

  const checkDependencyConflicts = useCallback(
    (movedTaskId, newStartMins, newDurationMins) => {
      const newEndMins = newStartMins + newDurationMins;
      const conflicts = [];

      tasks.forEach((task) => {
        if (task.id === movedTaskId) return;
        if (!task.scheduledStartTime) return;

        const movedTask = tasks.find((t) => t.id === movedTaskId);
        const isDependent = (workflowConnections || []).some(
          (conn) =>
            conn.sourceStageId === movedTask?.stageId &&
            conn.targetStageId === task.stageId,
        );

        if (!isDependent) return;

        const depStart = new Date(task.scheduledStartTime);
        const depStartMins = depStart.getHours() * 60 + depStart.getMinutes();

        if (newEndMins > depStartMins) {
          conflicts.push({
            taskId: task.id,
            stageName: task.stageName,
            message: `${task.stageName} starts at ${formatTimeDisplay(
              minutesToTimeStr(depStartMins),
            )} but predecessor won't finish until ${formatTimeDisplay(
              minutesToTimeStr(newEndMins),
            )}`,
          });
        }
      });

      return conflicts;
    },
    [tasks, workflowConnections],
  );

  const showToast = (msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 3000);
  };

  const handleDragStart = (e, task) => {
    if (!isAdmin) return;
    const style = getTaskStyle(task);
    if (!style) return;

    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset(e.clientY - rect.top);
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("taskId", task.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!draggingId || !calendarRef.current) return;

    const calRect = calendarRef.current.getBoundingClientRect();
    const relativeY = e.clientY - calRect.top - dragOffset;
    const minutesFromStart = (relativeY / SLOT_HEIGHT) * 60;

    const snappedMins =
      Math.round(minutesFromStart / SNAP_MINUTES) * SNAP_MINUTES +
      workStartMins;
    const maxAllowedMins = workEndMins + OVERTIME_BUFFER_HOURS * 60 - 30;
    const clampedMins = Math.max(
      workStartMins,
      Math.min(maxAllowedMins, snappedMins),
    );

    setGhostTop(((clampedMins - workStartMins) / 60) * SLOT_HEIGHT);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("taskId");
    const task = tasks.find((t) => t.id === taskId);
    if (!task || !calendarRef.current) return;

    const calRect = calendarRef.current.getBoundingClientRect();
    const relativeY = e.clientY - calRect.top - dragOffset;
    const minutesFromStart = (relativeY / SLOT_HEIGHT) * 60;

    const snappedMins =
      Math.round(minutesFromStart / SNAP_MINUTES) * SNAP_MINUTES +
      workStartMins;
    const clampedMins = Math.max(
      workStartMins,
      Math.min(workEndMins - 30, snappedMins),
    );

    const style = getTaskStyle(task);
    if (!style) return;

    const depConflicts = checkDependencyConflicts(
      taskId,
      clampedMins,
      style.durationMins,
    );
    setDependencyConflicts(depConflicts);

    if (depConflicts.length > 0) {
      showToast(
        `? Conflict: ${depConflicts[0].message}. Admin must reschedule manually.`,
      );
    }

    const newEndMins = clampedMins + style.durationMins;
    const isOvertime = newEndMins > workEndMins;

    const originalDate = new Date(task.scheduledStartTime);
    const newStartTime = new Date(originalDate);

    if (isNaN(newStartTime.getTime())) {
      showToast("Could not determine task date. Please reassign the task.");
      setDraggingId(null);
      setGhostTop(null);
      setDragOffset(0);
      return;
    }

    newStartTime.setHours(Math.floor(clampedMins / 60), clampedMins % 60, 0, 0);

    const updatedTasks = tasks.map((t) => {
      if (t.id === taskId) {
        return { ...t, scheduledStartTime: newStartTime.toISOString() };
      }
      return t;
    });

    const sorted = [...updatedTasks]
      .filter((t) => t.scheduledStartTime)
      .sort(
        (a, b) =>
          new Date(a.scheduledStartTime) - new Date(b.scheduledStartTime),
      );

    await onReorder(
      sorted.map((t, idx) => ({
        id: t.id,
        queuePosition: idx + 1,
        scheduledStartTime: t.scheduledStartTime,
        isOvertime: t.id === taskId ? isOvertime : t.isOvertime,
      })),
    );

    setDraggingId(null);
    setGhostTop(null);
    setDragOffset(0);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setGhostTop(null);
    setDragOffset(0);
  };

  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const showNowLine = nowMins >= workStartMins && nowMins <= workEndMins;
  const nowTop = ((nowMins - workStartMins) / 60) * SLOT_HEIGHT;

  return (
    <div className="hourly-calendar">
      {toast && <div className="calendar-toast">{toast}</div>}

      {tasks.some((t) => !t.scheduledStartTime) && (
        <div className="calendar-untimed">
          <div className="calendar-untimed-label">No time set</div>
          <UnscheduledTaskList
            tasks={tasks.filter((t) => !t.scheduledStartTime)}
            onOpen={onTaskClick}
          />
        </div>
      )}

      <div className="calendar-body">
        <div className="hour-labels">
          {hourSlots.map((slot) => (
            <div
              key={slot.minutes}
              className="hour-label"
              style={{ height: SLOT_HEIGHT }}
            >
              {slot.label}
            </div>
          ))}
        </div>

        <div
          className="calendar-grid"
          ref={calendarRef}
          style={{ height: calendarHeight }}
          onDragOver={isAdmin ? handleDragOver : undefined}
          onDrop={isAdmin ? handleDrop : undefined}
        >
          {hourSlots.map((slot) => (
            <div
              key={slot.minutes}
              className="hour-line"
              style={{
                top: ((slot.minutes - workStartMins) / 60) * SLOT_HEIGHT,
              }}
            />
          ))}

          {hourSlots.slice(0, -1).map((slot) => (
            <div
              key={`half-${slot.minutes}`}
              className="half-hour-line"
              style={{
                top: ((slot.minutes - workStartMins + 30) / 60) * SLOT_HEIGHT,
              }}
            />
          ))}

          <div
            className="work-end-line"
            style={{ top: calendarHeight }}
            title={`Work ends at ${workEndTime}`}
          />

          <div className="overtime-zone" style={{ top: calendarHeight }} />

          {showNowLine && (
            <div className="now-line" style={{ top: nowTop }}>
              <div className="now-dot" />
            </div>
          )}

          {isAdmin && ghostTop !== null && draggingId && (
            <div
              className="drag-ghost"
              style={{
                top: ghostTop,
                height:
                  getTaskStyle(tasks.find((t) => t.id === draggingId))
                    ?.height || MIN_TASK_HEIGHT,
              }}
            />
          )}

          {overlapGroups.map((group) =>
            group.map(({ task, style, column, columnCount }) => {
              if (!style) return null;

              const isDragging = task.id === draggingId;
              const statusInfo = formatAssignmentStatus(task.status);
              const statusStyle = getStatusStyle(
                task.status,
                task.slaPercentage,
              );
              const hasDepConflict = dependencyConflicts.some(
                (c) => c.taskId === task.id,
              );
              // 96% of the grid, split evenly across the cluster's lanes with a
              // 1% gutter between them.
              const laneSpan = 96 / columnCount;
              const columnWidth = `${laneSpan - (columnCount > 1 ? 1 : 0)}%`;
              const columnLeft = `${2 + column * laneSpan}%`;

              return (
                <div
                  key={task.id}
                  className={[
                    "calendar-task",
                    isDragging ? "calendar-task--dragging" : "",
                    task.isOvertime ? "calendar-task--overtime" : "",
                    hasDepConflict ? "calendar-task--conflict" : "",
                    columnCount > 1 ? "calendar-task--overlap" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    top: style.top,
                    height: style.height,
                    width: columnWidth,
                    left: columnLeft,
                    backgroundColor: statusStyle.backgroundColor,
                    borderLeft: `4px solid ${statusStyle.borderColor}`,
                  }}
                  draggable={isAdmin}
                  onDragStart={
                    isAdmin ? (e) => handleDragStart(e, task) : undefined
                  }
                  onDragEnd={isAdmin ? handleDragEnd : undefined}
                  onClick={() => onTaskClick?.(task)}
                  title={isAdmin ? "Drag to reschedule" : undefined}
                >
                  <div className="calendar-task-header">
                    {isAdmin && <span className="drag-handle-icon">?</span>}
                    <span className="calendar-task-time">
                      {task.scheduledStartTime
                        ? toLocalTimeDisplay(task.scheduledStartTime)
                        : formatTimeDisplay(minutesToTimeStr(style.startMins))}
                    </span>
                    {task.isOvertime && (
                      <span className="overtime-badge">OT</span>
                    )}
                    {hasDepConflict && (
                      <span
                        className="conflict-badge"
                        title="Dependency conflict"
                      >
                        ?
                      </span>
                    )}
                  </div>
                  <div className="calendar-task-name">{task.stageName}</div>
                  <div className="calendar-task-project">
                    {task.projectName}
                  </div>
                  {style.height > 60 && (
                    <div
                      className="calendar-task-status"
                      style={{ color: statusInfo.textColor }}
                    >
                      {statusInfo.icon} {statusInfo.label}
                    </div>
                  )}
                  {style.height > 80 && task.estimatedDuration && (
                    <div className="calendar-task-duration">
                      {task.durationUnit === "Hours"
                        ? `${task.estimatedDuration}h`
                        : `${task.estimatedDuration}d`}
                    </div>
                  )}
                </div>
              );
            }),
          )}


        </div>
      </div>
    </div>
  );
}
